import { useLazyQuery } from "@apollo/client";
import { AUTH_INFO, ShowToastError } from "@platformx/utilities";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import AssetApi from "../../services/assetsApi/assets.api";

const COMMUNITY = {
  collections: [],
  subcommunities: [],
};
const COLLECTION_ITEM = {
  collectionItem: [],
  page: {},
};
const useAsset = (sort = "DESC", playbackFilter = "ALL") => {
  const [assetData, setAssetData] = useState<any>(COMMUNITY);
  const [collectionItem, setCollectionItem] = useState<any>(COLLECTION_ITEM);
  const [folderLoading, setFolderLoading] = useState(false);
  const [damFolderTree, setDamFolderTree] = useState<any[]>([]);
  const [damFolderTreeLoading, setDamFolderTreeLoading] = useState(false);
  const { t } = useTranslation();
  const [getData, { data, loading: assetLoading }] = useLazyQuery(AssetApi.FETCH_COLLECTION_ITEM);
  const [getCommunityData] = useLazyQuery(AssetApi.FETCH_COMMUNITY_COLLECTION);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [isLazyLoad, setIsLazyLoad] = useState<boolean>(true);
  const [animationState, setAnimationState] = useState(false);

  const ROWS = 16;
  const [searchParams] = useSearchParams();
  const pathName = window.location.pathname.split("/");
  const assetType = pathName[pathName.length - 1] || "image";
  const assetUUID = {
    image: AUTH_INFO.dspaceImagesUuid, //changed images to image so that assetmanger works.
    video: AUTH_INFO.dspaceVideosUuid,
    document: AUTH_INFO.dspaceDocumentsUuid,
  };

  const uuid1 = searchParams.get("uuid1") || assetUUID[assetType];
  // The move-to-folder tree is always rooted at the asset type's own community,
  // even after drilling into a folder, so every parent/child folder stays reachable.
  const folderTreeRootUuid: string | undefined = assetUUID[assetType] || uuid1;
  // A site whose Global Config has no DAM community mapping resolves to "".
  // Fail closed: skip every DSpace call rather than querying an empty community.
  const isDamConfigured = Boolean(uuid1);

  const isVideoAsset = assetType?.toLowerCase?.() === "video";
  const isDocumentAsset = assetType?.toLowerCase?.() === "document";
  const playbackStatusFilter = isVideoAsset ? playbackFilter : "ALL";

  const firstCollectionUuid: string | undefined = assetData.collections?.[0]?.uuid;
  // Documents are queried at the community (misc) level, so use uuid1 instead of
  // collections[0].uuid. Images/videos keep using the collection uuid. Both the
  // fetch and the effect that triggers it must key off this same value, otherwise
  // the initial load stays blank while a manual refresh populates it.
  const listingUuid: string | undefined = isDocumentAsset ? uuid1 : firstCollectionUuid;

  const fetchDamFolderTree = useCallback(async () => {
    if (!folderTreeRootUuid) {
      setDamFolderTree([]);
      return;
    }
    setDamFolderTreeLoading(true);

    const loadTree = async (communityUuid: string) => {
      const { authoring_getAssets = {} } = await AssetApi.fetchDAMContent({
        uuid: communityUuid,
        start: 0,
        rows: 50,
        search: "",
        entityType: "ALL",
        assetType: "SUBCOMMUNITIES",
        tags: {},
        sortBy: sort,
        playbackStatus: "ALL",
      });
      setDamFolderTree(authoring_getAssets?.subcommunitiesList || []);
    };

    try {
      await loadTree(folderTreeRootUuid);
    } catch (err: any) {
      // Some sites' Global Config maps `misc` to a uuid that is not a real DSpace
      // community, and the SUBCOMMUNITIES query 404s on it. The Documents tab used
      // to read the video community unconditionally, so it never hit this. Fall
      // back to that older behaviour rather than showing the editor an error --
      // wrong folders beat a broken tab. Remove this only once every site's
      // document mapping is valid.
      if (isDocumentAsset) {
        try {
          await loadTree(AUTH_INFO.dspaceVideosUuid);
          return;
        } catch {
          /* fall through to the error path below */
        }
      }
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
      setDamFolderTree([]);
    } finally {
      setDamFolderTreeLoading(false);
    }
  }, [folderTreeRootUuid, isDocumentAsset, sort, t]);

  const fetchCommunityCollect = useCallback(async () => {
    if (!uuid1) {
      setAssetData(COMMUNITY);
      return;
    }
    try {
      setFolderLoading(true);
      const {
        data: { authoring_getAssets },
      } = await getCommunityData({
        variables: {
          uuid: uuid1,
          start: 0,
          rows: 20,
          search: "",
          sort: sort,
        },
      });
      if (authoring_getAssets) {
        const { collections = [], subcommunities = [] } = authoring_getAssets;
        if (collections?.[0]?.uuid) {
          setIsLazyLoad(true);
        } else {
          setIsLazyLoad(false);
        }
        setAssetData({
          collections,
          subcommunities,
        });
      }
      // Return the response
      return authoring_getAssets;
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    } finally {
      setFolderLoading(false);
    }
  }, [getCommunityData, sort, t, uuid1]);

  const fetchCollectionAsset = useCallback(() => {
    if (!listingUuid) return;
    setCollectionItem(COLLECTION_ITEM);
    getData({
      variables: {
        uuid: listingUuid,
        start: 0,
        rows: ROWS,
        search: "",
        entityType:
          assetType?.toLowerCase() === "image"
            ? "Image"
            : assetType?.toLowerCase() === "video"
              ? "Video"
              : "Document",
        sort: sort,
        playbackStatus: playbackStatusFilter,
      },
    });
    setStartIndex(0);
  }, [ROWS, assetType, listingUuid, getData, playbackStatusFilter, sort]);

  const fetchMoreData = async (index = 0) => {
    try {
      const { authoring_getAssets = {} } = await AssetApi.fetchCollectionItem({
        uuid: listingUuid || "",
        start: index,
        rows: ROWS,
        search: "",
        entityType:
          assetType?.toLowerCase() === "image"
            ? "Image"
            : assetType?.toLowerCase() === "video"
              ? "Video"
              : "Document",
        sort: sort,
        playbackStatus: playbackStatusFilter,
      });
      if (authoring_getAssets) {
        const { collectionItem: item = [], page = {} } = authoring_getAssets;
        if (item.length < ROWS) {
          setIsLazyLoad(false);
        } else {
          setIsLazyLoad(true);
        }

        setCollectionItem((prev) => ({
          collectionItem: [...prev.collectionItem, ...item],
          page: page,
        }));
      }
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    }
  };
  const handleRefresh = async () => {
    setAssetData({ collections: [...assetData.collections], subcommunities: [] });
    setAnimationState(true);
    fetchCommunityCollect();
    fetchDamFolderTree();
    try {
      if (listingUuid) {
        const { data: queryData } = await getData({
          variables: {
            uuid: listingUuid,
            start: 0,
            rows: ROWS,
            search: "",
            entityType:
              assetType?.toLowerCase() === "image"
                ? "Image"
                : assetType?.toLowerCase() === "video"
                  ? "Video"
                  : "Document",
            sort: sort,
            playbackStatus: playbackStatusFilter,
          },
        });
        const { authoring_getAssets } = queryData || {};
        if (authoring_getAssets) {
          const { collectionItem: item = [], page = {} } = authoring_getAssets;
          setIsLazyLoad(item.length >= ROWS);
          setCollectionItem({ collectionItem: item, page });
        }
      }
      setStartIndex(0);
    } catch (err: any) {
      const statusCode = err?.networkError?.statusCode || 0;
      ShowToastError(t("api_error_toast"), statusCode);
    } finally {
      setTimeout(() => {
        setAnimationState(false);
      }, 1000);
    }
  };
  useEffect(() => {
    if (listingUuid) {
      fetchCollectionAsset();
    } else {
      setCollectionItem(COLLECTION_ITEM);
    }
  }, [fetchCollectionAsset, listingUuid]);

  useEffect(() => {
    setAssetData(COMMUNITY);
    setCollectionItem(COLLECTION_ITEM);
    fetchCommunityCollect();
    fetchDamFolderTree();
    setStartIndex(0);
  }, [fetchCommunityCollect, fetchDamFolderTree, sort, uuid1]);

  useEffect(() => {
    const { authoring_getAssets } = data || {};
    if (authoring_getAssets) {
      const { collectionItem: item = [], page = {} } = authoring_getAssets;
      if (item.length < ROWS) {
        setIsLazyLoad(false);
      } else {
        setIsLazyLoad(true);
      }
      setCollectionItem({
        collectionItem: item,
        page: page,
      });
    }
  }, [data]);

  return {
    assetData,
    collectionItem,
    isDamConfigured,
    folderLoading,
    isLazyLoad,
    fetchMoreData,
    startIndex,
    setStartIndex,
    assetLoading,
    fetchCommunityCollect,
    fetchCollectionAsset,
    handleRefresh,
    animationState,
    damFolderTree,
    damFolderTreeLoading,
    fetchDamFolderTree,
  };
};

export default useAsset;
